import { motion } from "framer-motion";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { searchResources } from "../../../data/resources";

function ResourcesHero() {
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  
  const commonSearchTerms = [
    { text: "smart folders", url: "/resources/features-settings#smart-folders" },
    { text: "email templates", url: "/resources/features-settings#email-templates" },
    { text: "unsubscribe", url: "/resources/features-settings#unsubscribe" },
    { text: "inbox zero", url: "/resources/best-practices#inbox-zero" },
    { text: "gmail", url: "/resources/email-provider-integration#connecting-gmail" }
  ];

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (searchQuery.trim()) {
      // We can implement advanced search functionality later
      // For now, just search and navigate to first result if found
      const results = searchResources(searchQuery);
      
      if (results.length > 0) {
        const firstResult = results[0];
        navigate(`/resources/${firstResult.categoryId}#${firstResult.id}`);
      } else {
        // Could show "no results" message
        console.log("No results found");
      }
    }
  };

  return (
    <section className="py-16 md:py-24 px-4 relative">
      <div className="container mx-auto max-w-4xl text-center">
        <motion.h1 
          className="text-4xl md:text-6xl font-bold mb-12 text-[#694e42]"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          We've got your back 🚀
        </motion.h1>
        
        {/* Search Bar */}
        <motion.form
          className="relative mb-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          onSubmit={handleSearch}
        >
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
              </svg>
            </div>
            <input
              type="text"
              className="w-full py-4 pl-12 pr-4 rounded-full border border-[#e2dad3] focus:outline-none focus:ring-2 focus:ring-[#ff8c42]/50 focus:border-[#ff8c42] transition-colors text-[#694e42] bg-background/95 backdrop-blur-sm"
              placeholder="Search articles (ex. smart folders, inbox zero, gmail setup, etc.)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button 
              type="submit"
              className="absolute right-2 top-2 bottom-2 px-6 bg-[#ff8c42] text-white rounded-full font-medium hover:bg-[#ff7a29] transition-colors"
            >
              Search
            </button>
          </div>
        </motion.form>
        
        {/* Common Search Terms */}
        <motion.div
          className="text-[#694e42]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <span className="font-medium">People often look for: </span>
          {commonSearchTerms.map((term, index) => (
            <span key={index}>
              <a 
                href={term.url} 
                className="text-[#ff8c42] hover:underline"
              >
                {term.text}
              </a>
              {index < commonSearchTerms.length - 1 && <span className="mx-1.5">,</span>}
            </span>
          ))}
        </motion.div>
      </div>
      
      {/* Background Elements */}
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <motion.div 
          className="absolute w-24 h-24 text-[#ff8c42]/10 top-20 left-1/4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-full h-full">
            <path d="M11.47 3.84a.75.75 0 011.06 0l8.69 8.69a.75.75 0 101.06-1.06l-8.689-8.69a2.25 2.25 0 00-3.182 0l-8.69 8.69a.75.75 0 001.061 1.06l8.69-8.69z" />
            <path d="M12 5.432l8.159 8.159c.03.03.06.058.091.086v6.198c0 1.035-.84 1.875-1.875 1.875H15a.75.75 0 01-.75-.75v-4.5a.75.75 0 00-.75-.75h-3a.75.75 0 00-.75.75V21a.75.75 0 01-.75.75H5.625a1.875 1.875 0 01-1.875-1.875v-6.198a2.29 2.29 0 00.091-.086L12 5.43z" />
          </svg>
        </motion.div>
        
        <motion.div 
          className="absolute w-32 h-32 text-[#946e5d]/10 bottom-20 right-1/4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-full h-full">
            <path d="M21.721 12.752a9.711 9.711 0 00-.945-5.003 12.754 12.754 0 01-4.339 2.708 18.991 18.991 0 01-.214 4.772 17.165 17.165 0 005.498-2.477zM14.634 15.55a17.324 17.324 0 00.332-4.647c-.952.227-1.945.347-2.966.347-1.021 0-2.014-.12-2.966-.347a17.515 17.515 0 00.332 4.647 17.385 17.385 0 005.268 0zM9.772 17.119a18.963 18.963 0 004.456 0A17.182 17.182 0 0112 21.724a17.18 17.18 0 01-2.228-4.605zM7.777 15.23a18.87 18.87 0 01-.214-4.774 12.753 12.753 0 01-4.34-2.708 9.711 9.711 0 00-.944 5.004 17.165 17.165 0 005.498 2.477zM21.356 14.752a9.765 9.765 0 01-7.478 6.817 18.64 18.64 0 001.988-4.718 18.627 18.627 0 005.49-2.098zM2.644 14.752c1.682.971 3.53 1.688 5.49 2.099a18.64 18.64 0 001.988 4.718 9.765 9.765 0 01-7.478-6.816zM13.878 2.43a9.755 9.755 0 016.116 3.986 11.267 11.267 0 01-3.746 2.504 18.63 18.63 0 00-2.37-6.49zM12 2.276a17.152 17.152 0 012.805 7.121c-.897.23-1.837.353-2.805.353-.968 0-1.908-.122-2.805-.353A17.151 17.151 0 0112 2.276zM10.122 2.43a18.629 18.629 0 00-2.37 6.49 11.266 11.266 0 01-3.746-2.504 9.754 9.754 0 016.116-3.985z" />
          </svg>
        </motion.div>
      </div>
    </section>
  );
}

export default ResourcesHero;